import { TFunction } from "i18next";
import { VrLessonProgress } from "models/LessonProgress";
import { Node } from "models/Node";
import { NodeType } from "models/NodeType";
import { CompletedNode } from "models/CompletedNode";
import { RewardType } from "models/RewardType";
import { UserWorkspaceProfile } from "models/UserWorkspaceProfile";
import { formatDateToCustomString } from "helpers/dateHelper";

const CSV_DELIMITER = ";";
const CSV_BOM = "﻿";
const EMPTY_VALUE = "-";

export interface MyNodeReportParams {
    lp: VrLessonProgress;
    user: Pick<UserWorkspaceProfile, "displayName" | "tagA" | "tagB">;
    nodes: Node[];
    completedNodes: CompletedNode[];
    feedback: string | null;
    reward: RewardType | null;
    t: TFunction;
}

const escapeCell = (value: unknown): string => {
    const str = value === null || value === undefined ? "" : String(value);
    return `"${str.replace(/"/g, '""')}"`;
};

const toRow = (cells: unknown[]): string => cells.map(escapeCell).join(CSV_DELIMITER);

const TABLE_COLUMNS = 6;

const rewardLabel = (rewardType: RewardType, t: TFunction): string => {
    switch (rewardType) {
        case RewardType.Gold:
            return t("rewardType.gold");
        case RewardType.Silver:
            return t("rewardType.silver");
        case RewardType.Bronze:
            return t("rewardType.bronze");
        default:
            return t("rewardType.none");
    }
};

/** Formats the elapsed time between session start and completion as mm:ss (or hh:mm:ss). */
const sessionDuration = (createdAt: string, completedAt: string | null): string => {
    if (!completedAt) {
        return EMPTY_VALUE;
    }

    const diffMs = new Date(completedAt).getTime() - new Date(createdAt).getTime();
    if (!Number.isFinite(diffMs) || diffMs < 0) {
        return EMPTY_VALUE;
    }

    const totalSeconds = Math.floor(diffMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (n: number) => String(n).padStart(2, "0");

    return hours > 0
        ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
        : `${pad(minutes)}:${pad(seconds)}`;
};

interface ReportAnswer {
    label: string;
    score: number;
    isChosen: boolean;
}

const buildAnswers = (node: Node, completedNodes: CompletedNode[]): ReportAnswer[] => {
    const chosenAnswerId = completedNodes.find(cn => cn.nodeId === node.id)?.answerModel?.answerId;

    const textAnswers: ReportAnswer[] = node.textAnswers.map(a => ({
        label: a.answer,
        score: a.score,
        isChosen: a.id === chosenAnswerId,
    }));

    const voiceAnswers: ReportAnswer[] = node.voiceAnswers.map(a => ({
        label: a.keywords,
        score: a.score,
        isChosen: a.id === chosenAnswerId,
    }));

    return [...textAnswers, ...voiceAnswers];
};

export const buildMyNodeReportCsv = (params: MyNodeReportParams): string => {
    const { lp, user, nodes, completedNodes, feedback, reward, t } = params;

    const version = lp.lessonVersion;
    const chosenMark = t("myTrainings.report.chosenMark");
    const rows: string[] = [];

    // --- Header block ------------------------------------------------------
    rows.push(toRow([t("myTrainings.report.participant"), user.displayName ?? ""]));
    rows.push(toRow([t("myTrainings.report.tags"), user.tagA ?? "", user.tagB ?? ""]));
    rows.push(toRow([t("myTrainings.report.training"), `${version.lessonName} (v${version.version})`]));
    rows.push(toRow([
        t("myTrainings.report.sessionDateTime"),
        formatDateToCustomString(lp.createdAt),
        formatDateToCustomString(lp.createdAt, true).split(" ")[1] ?? "",
    ]));
    rows.push(toRow([t("myTrainings.report.sessionDuration"), sessionDuration(lp.createdAt, lp.completedAt)]));
    rows.push(toRow([t("myTrainings.report.points"), `${lp.pointsScored}/${version.maxPoints}`]));
    rows.push(toRow([t("myTrainings.report.label"), rewardLabel(reward ?? RewardType.None, t)]));

    // --- Session course table ---------------------------------------------
    rows.push("");
    rows.push(toRow([t("myTrainings.report.sessionCourse")]));
    rows.push(toRow([
        t("myTrainings.report.interaction"),
        t("myTrainings.report.sequenceType"),
        t("myTrainings.report.question"),
        t("myTrainings.report.answers"),
        t("myTrainings.report.participantChoice"),
        t("myTrainings.report.pointsColumn"),
    ]));

    const interactionNodes = nodes.filter(n => n.type === NodeType.Sequence);

    interactionNodes.forEach((node, index) => {
        const interactionNumber = index + 1;
        const answers = buildAnswers(node, completedNodes);

        if (answers.length === 0) {
            rows.push(toRow([
                interactionNumber,
                t("myTrainings.report.sequenceTextSlide"),
                EMPTY_VALUE,
                EMPTY_VALUE,
                EMPTY_VALUE,
                "",
            ]));
            return;
        }

        answers.forEach((answer, answerIndex) => {
            const isFirst = answerIndex === 0;
            rows.push(toRow([
                isFirst ? interactionNumber : "",
                isFirst ? t("myTrainings.report.sequenceVideoQuestion") : "",
                isFirst ? node.textQuestion : "",
                `${answerIndex + 1}. ${answer.label}`,
                answer.isChosen ? chosenMark : "",
                answer.score,
            ]));
        });
    });

    rows.push("");
    rows.push(toRow([t("myTrainings.report.feedbackAfter")]));
    rows.push(toRow([feedback ?? EMPTY_VALUE]));

    const normalized = rows.map(row => {
        if (row === "") {
            return "";
        }
        const missing = TABLE_COLUMNS - (row.match(new RegExp(CSV_DELIMITER, "g")) ?? []).length - 1;
        return missing > 0 ? row + CSV_DELIMITER.repeat(missing) : row;
    });

    return CSV_BOM + normalized.join("\r\n");
};

const sanitizeFileName = (value: string): string =>
    value.replace(/[\\/:*?"<>|]/g, "_").replace(/\s+/g, "_").trim();

export const buildMyNodeReportFileName = (lp: VrLessonProgress, t: TFunction): string => {
    const prefix = t("myTrainings.report.fileNamePrefix");
    const name = sanitizeFileName(lp.lessonVersion.lessonName || "report");
    const date = formatDateToCustomString(lp.createdAt).replace(/\//g, "-");
    return `${prefix}_${name}_${date}.csv`;
};

export const downloadCsv = (csv: string, fileName: string): void => {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

export const generateMyNodeReport = (params: MyNodeReportParams): void => {
    const csv = buildMyNodeReportCsv(params);
    const fileName = buildMyNodeReportFileName(params.lp, params.t);
    downloadCsv(csv, fileName);
};
